// Paso 5 del asistente (PE-A3b): plan de acción (emergency_plan_actions). Lógica pura, sin base de datos.
//  - Acciones OBLIGATORIAS automáticas: cada no_cumple, el punto de encuentro "no apto" o moderado o peor y la
//    vulnerabilidad alta o muy alta (pe-paso3.ts). Cada una lleva su origen y una clave única (source_key):
//    al guardar de nuevo no se duplica; si el origen deja de fallar, queda marcada "origen resuelto" (no se
//    borra); si vuelve a fallar, se reabre la marca.
//  - Acciones manuales: sin clave de origen.
//  - Una acción obligatoria no se guarda sin responsable (trabajador o cargo) ni fecha compromiso.
//  - Todas cuentan como evidencia de PO-18 ("Implementación del plan de emergencia in situ"); annex_item =
//    'PO-18' (M35). El puntaje del Checklist NO cambia en este bloque.

import type { AccionDerivada } from './pe-paso3';

export type EstadoAccion = 'pendiente' | 'en_curso' | 'cerrada';
export const ESTADOS_ACCION: EstadoAccion[] = ['pendiente', 'en_curso', 'cerrada'];
export const ETIQUETA_ESTADO_ACCION: Record<EstadoAccion, string> = { pendiente: 'Pendiente', en_curso: 'En curso', cerrada: 'Cerrada' };
export const ITEM_PO18 = 'PO-18';

// Columnas de M33 + M35.
export interface FilaAccion {
  id: string;
  company_id: string;
  plan_id: string;
  sector_id: string | null;
  source_method: string | null;
  req: string | null;
  norma: string | null;
  articulo: string | null;
  description: string;
  responsible_worker_id: string | null;
  due_date: string | null;
  status: EstadoAccion;
  evidence_path: string | null;
  closed_at: string | null;
  closed_by: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // M35
  source_key: string | null;
  is_mandatory: boolean;
  source_resolved_at: string | null;
  responsible_role: string | null;
  annex_item: string | null;
}

export interface AccionEditable {
  id?: string;
  sourceKey: string | null;
  sourceMethod: string | null;
  sectorId: string | null;
  norma: string | null;
  articulo: string | null;
  descripcion: string;
  obligatoria: boolean;
  responsableId: string | null;
  responsableCargo: string;
  fecha: string;          // AAAA-MM-DD o ''
  estado: EstadoAccion;
  // PE-A4: ¿tiene evidencia guardada (M37 o evidence_path)? Una obligatoria no se cierra sin ella.
  conEvidencia?: boolean;
}

export const MENSAJE_CIERRE_SIN_EVIDENCIA =
  'Una acción obligatoria no se puede cerrar sin evidencia: adjunta al menos una foto o un PDF y después márcala como cerrada.';

export function validarAccion(a: AccionEditable): string[] {
  const e: string[] = [];
  if (!a.descripcion.trim()) e.push('La acción necesita una descripción.');
  if (!ESTADOS_ACCION.includes(a.estado)) e.push('Estado no válido.');
  if (a.fecha && !/^\d{4}-\d{2}-\d{2}$/.test(a.fecha)) e.push('Fecha compromiso no válida.');
  if (a.obligatoria) {
    if (!a.responsableId && !a.responsableCargo.trim()) e.push('Una acción obligatoria necesita responsable (trabajador o cargo).');
    if (!a.fecha) e.push('Una acción obligatoria necesita fecha compromiso.');
    if (a.estado === 'cerrada' && !a.conEvidencia) e.push(MENSAJE_CIERRE_SIN_EVIDENCIA);
  }
  return e;
}

export const editableDesdeDerivada = (d: AccionDerivada): AccionEditable => ({
  sourceKey: d.sourceKey, sourceMethod: d.sourceMethod, sectorId: d.sectorId, norma: d.norma, articulo: d.articulo,
  descripcion: d.descripcion, obligatoria: true, responsableId: null, responsableCargo: '', fecha: '', estado: 'pendiente',
});

export const editableDesdeFila = (f: FilaAccion): AccionEditable => ({
  id: f.id, sourceKey: f.source_key, sourceMethod: f.source_method, sectorId: f.sector_id, norma: f.norma, articulo: f.articulo,
  descripcion: f.description, obligatoria: f.is_mandatory, responsableId: f.responsible_worker_id,
  responsableCargo: f.responsible_role ?? '', fecha: f.due_date ?? '', estado: f.status,
});

export interface Sincronizacion {
  propuestas: AccionDerivada[];   // obligatorias que aún no están guardadas (falta responsable y fecha)
  resolver: string[];             // ids guardados cuyo origen ya no falla → marcar "origen resuelto"
  reabrir: string[];              // ids marcados resueltos cuyo origen volvió a fallar
}

// ¿La acción guardada quedó "resuelta por cambio de origen"? Manda el cálculo de hoy (si el origen ya no falla
// está resuelta, aunque todavía no se haya guardado el paso 3); sin cálculo, la marca guardada (PE-A3c). Las
// manuales (sin clave de origen) nunca lo están.
// Las acciones de un simulacro (PE-B2, clave 'simulacro:<id>:<n>') no salen del paso 3: el cálculo no las resuelve.
const deSimulacro = (k: string | null) => !!k && k.startsWith('simulacro:');

export function origenResuelto(f: Pick<FilaAccion, 'source_key' | 'source_resolved_at'>, vigentes?: Set<string>): boolean {
  if (!f.source_key) return false;
  if (deSimulacro(f.source_key)) return !!f.source_resolved_at;
  return vigentes ? !vigentes.has(f.source_key) : !!f.source_resolved_at;
}

// Compara lo guardado con lo que pide hoy el cálculo. Nunca duplica: una clave guardada no se propone otra vez.
export function sincronizarAcciones(guardadas: Pick<FilaAccion, 'id' | 'source_key' | 'source_resolved_at'>[], derivadas: AccionDerivada[]): Sincronizacion {
  const actuales = new Set(derivadas.map(d => d.sourceKey));
  const conClave = guardadas.filter(g => g.source_key && !deSimulacro(g.source_key));
  const guardadasClave = new Set(conClave.map(g => g.source_key as string));
  const vistas = new Set<string>();
  const propuestas = derivadas.filter(d => {
    if (guardadasClave.has(d.sourceKey) || vistas.has(d.sourceKey)) return false;
    vistas.add(d.sourceKey);
    return true;
  });
  return {
    propuestas,
    resolver: conClave.filter(g => !actuales.has(g.source_key as string) && !g.source_resolved_at).map(g => g.id),
    reabrir: conClave.filter(g => actuales.has(g.source_key as string) && !!g.source_resolved_at).map(g => g.id),
  };
}
